/* Draft-theme presentation/URL adapter. Oxi retains wishlist data and authentication. */
(() => {
  const flag = 'data-ggx-wishlist-repairs';
  if (document.documentElement.hasAttribute(flag)) return;
  document.documentElement.setAttribute(flag, '');
  const panels = '.oxi-wl-popup,.oxi-wl-sidebar';

  const repairLoginLink = (link) => {
    const original = link.getAttribute('href') || '';
    const marker = original.indexOf('?checkout_url=');
    // Only fix the vendor's second question mark; normal URLs stay untouched.
    if (marker < 0 || original.indexOf('?') === marker) return;
    try {
      const url = new URL(original.slice(0, marker), location.origin);
      if (url.origin !== location.origin || url.pathname !== '/customer_authentication/redirect' || url.searchParams.has('checkout_url')) return;
      let destination = original.slice(marker + '?checkout_url='.length);
      // Preserve an already URL-encoded return path without double encoding it.
      if (/^(?:%2f|https?%3a)/i.test(destination)) destination = decodeURIComponent(destination);
      url.searchParams.set('checkout_url', destination);
      link.setAttribute('href', url.href);
    } catch (_) { /* Leave unrecognized vendor links unchanged. */ }
  };

  let helpCount = 0;
  const addFavoritesInfo = host => {
    const info = document.createElement('button');
    info.type = 'button';
    info.className = 'ggx-save-info';
    info.setAttribute('aria-label', 'About My Favorites');
    info.setAttribute('aria-expanded', 'false');
    const popover = document.createElement('div');
    popover.className = 'ggx-save-explainer';
    popover.id = `ggx-favorites-help-${++helpCount}`;
    popover.setAttribute('popover', 'auto');
    popover.setAttribute('role', 'region');
    popover.setAttribute('aria-label', 'My Favorites');
    popover.innerHTML = '<button type="button" class="ggx-save-info-close" aria-label="Close Favorites information">×</button><strong>Your favorites, easy to find again</strong><p>Save coffees to My Favorites to find them again. Your favorites stay in this browser, so you can return without searching.</p><p>No sign-in needed to save here.</p>';
    info.innerHTML = '<span aria-hidden="true">i</span>';
    info.setAttribute('popovertarget', popover.id);
    info.setAttribute('aria-controls', popover.id);
    host.append(info, popover);
    const position = () => {
      const rect = info.getBoundingClientRect();
      const width = Math.min(320, window.innerWidth - 24);
      popover.style.width = `${width}px`;
      popover.style.left = `${Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12))}px`;
      const height = popover.offsetHeight || 235;
      popover.style.top = `${Math.max(12, Math.min(rect.bottom + 8, window.innerHeight - height - 12))}px`;
    };
    info.addEventListener('click', position);
    popover.addEventListener('toggle', () => {
      const open = popover.matches(':popover-open');
      info.setAttribute('aria-expanded', String(open));
      if (open) position();
    });
    popover.querySelector('.ggx-save-info-close').addEventListener('click', () => { popover.hidePopover(); info.focus(); });
    popover.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      popover.hidePopover();
      info.focus();
    });
    info.addEventListener('keydown', event => {
      if (event.key === 'Escape' && popover.matches(':popover-open')) {
        event.preventDefault();
        event.stopPropagation();
        popover.hidePopover();
      }
    });
  };

  const addMenuFavoritesInfo = () => {
    document.querySelectorAll('a[href],button.wishlist-custom-trigger').forEach(link => {
      if (link.closest('.ggx-favorites-entry,.ggx-save-explainer') || link.hasAttribute('data-ggx-favorites-explained')) return;
      let isFavorites = link.matches('.wishlist-custom-trigger');
      if (link.matches('a[href]')) {
        try {
          const url = new URL(link.getAttribute('href'), location.origin);
          isFavorites = url.origin === location.origin && url.pathname.replace(/\/$/, '') === '/a/social-login/wishlist';
        } catch (_) { return; }
      }
      if (!isFavorites || link.closest('.oxi-wl-popup,.oxi-wl-sidebar') || link.querySelector('input,button')) return;
      const group = document.createElement('span');
      group.className = 'ggx-favorites-entry';
      link.before(group);
      group.append(link);
      addFavoritesInfo(group);
    });
  };

  // GemPages wraps its purchase button in a fixed-height gp-button. Oxi's
  // "below" placement can insert a second button inside that purchase button.
  // Keep the app-owned node/listeners, but give it its own normal-flow row.
  const repairGiftCardFavoriteButton = () => {
    if (!/\/products\/gift-card\/?$/.test(location.pathname)) return;
    document.querySelectorAll('gp-product gp-product-button button.oxi-wl-auto-injected-btn').forEach(button => {
      const purchase = button.closest('gp-product-button');
      if (!purchase?.parentElement) return;
      let row = purchase.nextElementSibling;
      if (!row?.classList.contains('ggx-gift-favorite-row')) {
        row = document.createElement('div');
        row.className = 'ggx-gift-favorite-row';
        purchase.after(row);
      }
      if (button.type !== 'button') button.type = 'button';
      button.classList.add('ggx-gift-favorite-button');
      row.append(button);
    });
  };

  const repairNestedProductButtons = () => {
    document.querySelectorAll('add-to-cart-component button[name="add"] button.oxi-wl-auto-injected-btn').forEach(button => {
      const component = button.closest('add-to-cart-component');
      if (!component?.parentElement) return;
      // Move the vendor-owned node, preserving its listeners and saved state.
      button.type = 'button';
      button.classList.add('ggx-wishlist-product-button');
      component.after(button);
    });
    // Keep the vendor-owned control and its listeners; only relocate and present it.
    document.querySelectorAll('button.ggx-wishlist-product-button.oxi-wl-auto-injected-btn').forEach(button => {
      if (button.type !== 'button') button.type = 'button';
      const details = button.closest('.product-details');
      if (!details || details.closest('.ggx-gift')) return;
      const heading = details.querySelector('h1');
      const host = details.querySelector('product-price');
      if (!heading || !host || host.closest('a,button')) return;
      // Restore the whole title width; the native price component replaces only its priceContainer.
      if (heading.parentElement?.classList.contains('ggx-title-save-row')) heading.parentElement.classList.remove('ggx-title-save-row');
      if (!host.classList.contains('ggx-price-save-row')) host.classList.add('ggx-price-save-row');
      if (button.parentElement !== host) host.append(button);
      if (!host.querySelector('.ggx-save-info')) addFavoritesInfo(host);

      const saveType = button.getAttribute('data-save-type');
      const saved = saveType ? saveType !== 'none' : Boolean(button.querySelector('.oxi-wl-filled'));
      let caption = button.querySelector('.ggx-save-caption');
      if (!caption) {
        caption = document.createElement('span');
        caption.className = 'ggx-save-caption';
        button.append(caption);
      }
      const wording = saved ? 'Saved' : 'Save';
      if (caption.textContent !== wording) caption.textContent = wording;
      const title = heading.textContent.trim().replace(/\s+/g, ' ');
      const label = `${saved ? 'Remove' : 'Save'} ${title} ${saved ? 'from' : 'to'} My Favorites`;
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
      if (button.getAttribute('aria-pressed') !== String(saved)) button.setAttribute('aria-pressed', String(saved));
      // Vendor labels remain in place for its code, but only our concise caption is visible.
      [...button.childNodes].forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) node.textContent = '';
      });
    });
  };

  const labelNativeCardButtons = () => {
    document.querySelectorAll('.oxi-wl-placeholder button.oxi-wl-card-btn').forEach(button => {
      const item = button.closest('li.product-grid__item');
      const productId = button.closest('.oxi-wl-placeholder')?.dataset.productId;
      // Recommendations inject inside product-card; collection grids may inject beside it.
      const card = button.closest('product-card[data-product-id]') ||
        [...(item?.querySelectorAll('product-card[data-product-id]') || [])]
          .find(card => productId && card.dataset.productId === productId);
      if (!card) return;
      const title = (card.querySelector('[ref="productTitleLink"]')?.textContent ||
        card.querySelector('[ref="productCardLink"]')?.getAttribute('aria-label') ||
        'this item').trim().replace(/\s+/g, ' ') || 'this item';
      // Oxi owns data-save-type; mirror it without changing its stored state.
      const saveType = button.getAttribute('data-save-type');
      const saved = saveType ? saveType !== 'none' : Boolean(button.querySelector('.oxi-wl-filled'));
      const label = `${saved ? 'Remove' : 'Save'} ${title} ${saved ? 'from' : 'to'} My Favorites`;
      if (button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
      if (button.getAttribute('aria-pressed') !== String(saved)) button.setAttribute('aria-pressed', String(saved));
      if (button.type !== 'button') button.type = 'button';
    });
  };

  // Theme-local wording only. Keep vendor IDs, URLs, listeners and saved data intact.
  const favoritesWording = value => value
    .replace(/\bmy coffee list\b/gi, 'My Favorites')
    .replace(/\byour wish\s*list is (currently )?empty\b/gi, 'Your Favorites are $1empty')
    .replace(/\bmy wish\s*lists?\b/gi, 'My Favorites')
    .replace(/\byour wish\s*lists?\b/gi, 'your Favorites')
    .replace(/\bwish\s*lists?\b/gi, 'Favorites');
  const labelFavorites = () => {
    const roots = [...document.querySelectorAll('[class*="oxi-wl-"],#oxi-floating-button-container')];
    const isFavoritesPage = location.pathname.replace(/\/$/, '') === '/a/social-login/wishlist';
    if (isFavoritesPage) {
      const main = document.querySelector('main');
      if (main) roots.push(main);
      const title = favoritesWording(document.title);
      if (document.title !== title) document.title = title;
    }
    const skip = 'script,style,textarea,input,[contenteditable],.oxi-wl-title-link,.oxi-wl-variant-title';
    const visited = new Set();
    roots.forEach(root => {
      if (root.closest(skip)) return;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        if (visited.has(node) || node.parentElement?.closest(skip)) continue;
        visited.add(node);
        const text = favoritesWording(node.data);
        if (node.data !== text) node.data = text;
      }
      [root, ...root.querySelectorAll('[aria-label],[title]')].forEach(element => {
        if (element.closest(skip)) return;
        for (const attr of ['aria-label', 'title']) {
          const current = element.getAttribute(attr);
          if (current) {
            const label = favoritesWording(current);
            if (current !== label) element.setAttribute(attr, label);
          }
        }
      });
    });
  };

  const repairPanels = () => {
    repairGiftCardFavoriteButton();
    repairNestedProductButtons();
    labelNativeCardButtons();
    labelFavorites();
    addMenuFavoritesInfo();
    document.querySelectorAll(panels).forEach(panel => {
      panel.querySelectorAll('a.oxi-wl-login-btn').forEach(repairLoginLink);
      if (!panel.textContent.includes('Loding your saved items')) return;
      const text = document.createTreeWalker(panel, NodeFilter.SHOW_TEXT);
      while (text.nextNode()) {
        const node = text.currentNode;
        if (node.parentElement?.closest('script,style,textarea,[contenteditable]')) continue;
        if (/^\s*Loding your saved items(?:\.{3}|…)?\s*$/.test(node.data)) {
          node.data = node.data.replace('Loding your saved items', 'Loading your saved items');
        }
      }
    });
  };

  const init = () => {
    repairPanels();
    // Animated scenery and clocks also mutate the body. Only vendor/product/menu
    // changes can affect these repairs; do not rescan every link for each particle.
    const relevantScope = '[class*="oxi-wl-"],#oxi-floating-button-container,.product-details,li.product-grid__item,.ggx-favorites-entry,.wishlist-custom-trigger';
    const insertedContent = `${relevantScope},a[href]`;
    const isFavoritesPage = location.pathname.replace(/\/$/, '') === '/a/social-login/wishlist';
    const needsRepair = record => {
      const target = record.target.nodeType === Node.ELEMENT_NODE ? record.target : record.target.parentElement;
      if (!target) return false;
      if (target.closest(relevantScope) || (isFavoritesPage && target.closest('main'))) return true;
      if (record.type === 'attributes') return record.attributeName === 'href' && target.matches('a');
      if (record.type !== 'childList') return false;
      return [...record.addedNodes].some(node => node.nodeType === Node.ELEMENT_NODE &&
        (node.matches(insertedContent) || node.querySelector(insertedContent)));
    };
    new MutationObserver(records => {
      if (records.some(needsRepair)) repairPanels();
    }).observe(document.body, {
      childList: true, subtree: true, characterData: true,
      attributes: true, attributeFilter: ['href', 'type', 'data-save-type', 'class', 'aria-label', 'title']
    });
    // Also cover a vendor href rewrite in the same task as a click.
    for (const type of ['click', 'auxclick']) document.addEventListener(type, event => {
      const link = event.target instanceof Element ? event.target.closest('a.oxi-wl-login-btn') : null;
      if (link?.closest(panels)) repairLoginLink(link);
    }, true);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
})();


